import { useParams, Link } from "react-router-dom";
import { ShieldAlert, Hand, Flag, ArrowRightLeft, Lock, Unlock, MessageCircle, Eye } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { api } from "@/lib/api";
import {
  Section, Loadable, Status, Info, History, ActionButton, RunButton, BackLink, PageView, PageRows, PageButtons, BlockedAside,
  useLoad, fmt, deText, label,
} from "@/components/control/kit";
import { ModButtons } from "@/pages/CommunityPostDetail";

const FLOW = {
  "übernommen": ["offen"], "in Bearbeitung": ["offen", "übernommen", "entschieden"],
  "entschieden": ["übernommen", "in Bearbeitung"], "gelöst": ["entschieden", "in Bearbeitung"], "wieder geöffnet": ["gelöst"],
};
const TO_LABEL = { "übernommen": "Übernommen", "in Bearbeitung": "In Bearbeitung", "entschieden": "Entscheidung festhalten",
  "gelöst": "Fall abschließen", "wieder geöffnet": "Erneut öffnen" };

// Matches require_write_allowed() in the Core: only these member actions are blocked by a write restriction.
const WRITE_ACTIONS = ["Nachrichten senden (privat und im Kreis)", "Private Unterhaltung beginnen",
  "Community-Beiträge erstellen und bearbeiten", "Kommentare erstellen und bearbeiten"];
const UNCHANGED = "Lesen, eigene Beiträge entfernen, Aktivitäten, Kreisbeitritt und Meldungen";

function RestrictionCompare({ reported }) {
  const before = `gesperrt bis ${fmt(reported.write_restricted_until)}`;
  return (
    <div className="overflow-x-auto" data-testid="lift-restriction-compare">
      <table className="w-full text-left text-sm">
        <thead><tr className="border-b border-border text-xs text-muted-foreground"><th className="py-1.5 pr-2">Recht</th><th className="py-1.5 pr-2">Vorher</th><th className="py-1.5">Nachher</th></tr></thead>
        <tbody>
          {WRITE_ACTIONS.map((a) => (
            <tr key={a} className="border-b border-border/60"><td className="py-1.5 pr-2">{a}</td><td className="py-1.5 pr-2 text-red-700">{before}</td><td className="py-1.5 text-green-800">erlaubt</td></tr>
          ))}
          <tr><td className="py-1.5 pr-2">{UNCHANGED}</td><td className="py-1.5 pr-2">erlaubt</td><td className="py-1.5">unverändert erlaubt</td></tr>
        </tbody>
      </table>
      {reported.write_restriction_reason && <p className="mt-2 text-xs text-zinc-700">Grund der Einschränkung (intern): {reported.write_restriction_reason}</p>}
    </div>
  );
}

function Actions({ d, id, reload }) {
  const r = d.report;
  const reported = d.context.reported;
  const base = `/reports/${id}`;
  const options = Object.keys(FLOW).filter((to) => FLOW[to].includes(r.status)).map((to) => ({ value: to, label: TO_LABEL[to] }));
  if (!d.can_moderate) return <Status value={r.status} />;
  return (
    <>
      <Status value={r.status} testId="report-status" />
      {r.status === "offen" && <RunButton label="Übernehmen" icon={Hand} testId="claim-report" success="Meldung übernommen." onDone={reload} run={() => api.post(`${base}/claim`)} />}
      <ActionButton label="Priorisieren" icon={Flag} testId="prioritize-report" onDone={reload} title="Meldung priorisieren" submitLabel="Priorität speichern"
        page="prioritaet" pageSubtitle="Die Priorität wird sachlich begründet und einer Rolle zugewiesen." pageSubject={deText(r.title)}
        pageRows={[["Fall", deText(r.title)], ["Kategorie", d.context.type], ["Aktuelle Priorität", label(r.severity)], ["Zuständigkeit", r.assigned_to?.name || "Noch nicht übernommen"]]}
        fields={[
          { name: "severity", label: "Dringlichkeit", type: "select", required: true, default: r.severity, options: [
            { value: "kritisch", label: "Kritisch – sofort prüfen" }, { value: "mittel", label: "Mittel" }, { value: "niedrig", label: "Niedrig" }] },
          { name: "reason", label: "Begründung", type: "textarea", required: true },
        ]}
        onSubmit={async (v) => { await api.post(`${base}/priority`, v); return "Dringlichkeit gespeichert."; }} />
      {options.length > 0 && (
        <ActionButton label="Status ändern" icon={ArrowRightLeft} testId="transition-report" onDone={reload} title="Fall abschliessen oder erneut öffnen"
          page="status" pageSubtitle="Ein Fallabschluss bleibt mit Ergebnis und Begründung nachvollziehbar." submitLabel="Speichern" pageSubject={deText(r.title)}
          pageRows={[["Fall", deText(r.title)], ["Status", label(r.status)], ["Ergebnis", r.decision || "Noch nicht festgehalten"],
            ["Erneut öffnen", "Nur mit nachvollziehbarer Begründung möglich"]]}
          fields={[
            { name: "to", label: "Nächster Schritt", type: "select", required: true, options },
            { name: "decision", label: "Entscheidung (nur bei „Entscheidung festhalten“)", type: "textarea" },
            { name: "reason", label: "Begründung / Abschlussbericht", type: "textarea", required: true },
          ]}
          onSubmit={async (v) => { await api.post(`${base}/transition`, v); return "Status geändert."; }} />
      )}
      {reported && !reported.write_restricted_until && (
        <ActionButton label="Vorübergehend einschränken" icon={Lock} danger testId="restrict-member" onDone={reload} submitLabel="Einschränkung bestätigen"
          page="einschraenken" title="Vorübergehende Einschränkung" pageSubtitle="Begrenze eine Massnahme auf das erforderliche Recht und den Zeitraum."
          pageRows={[["Betroffene Person", reported.name]]} pageSubject={reported.name}
          pageNotice={{ title: "Entscheidung noch offen", text: "Begründung und Nachricht vor einer bestätigten Massnahme prüfen." }}
          description={`Schreibrecht von ${reported.name} befristet einschränken. Lesen bleibt möglich.`}
          fields={[
            { name: "days", label: "Dauer", type: "select", required: true, default: "7", placeholder: false,
              options: [1, 3, 7, 14, 30].map((x) => ({ value: String(x), label: `${x} Tage` })) },
            { name: "reason", label: "Grund (intern)", type: "textarea", required: true },
            { name: "message", label: "Nachricht an die Person (optional)", type: "textarea" },
          ]}
          onSubmit={async (v) => { await api.post(`${base}/restrict`, { ...v, days: Number(v.days) }); return "Einschränkung gesetzt."; }} />
      )}
      {reported?.write_restricted_until && (
        <ActionButton label="Einschränkung aufheben" icon={Unlock} testId="lift-restriction" onDone={reload} submitLabel="Aufhebung bestätigen"
          page="aufheben" title="Einschränkung aufheben" pageSubtitle="Vergleiche die Rechte vor und nach der beabsichtigten Aufhebung."
          pageRows={[["Betroffene Person", reported.name]]} pageSubject={reported.name}
          description={`Schreibrecht von ${reported.name} wieder vollständig herstellen. Vergleich vorher/nachher:`}
          children={<RestrictionCompare reported={reported} />}
          fields={[
            { name: "reason", label: "Grund der Aufhebung", type: "textarea", required: true },
            { name: "notify", label: "Person benachrichtigen", type: "checkbox", default: true },
          ]}
          onSubmit={async (v) => { await api.post(`${base}/lift`, v); return "Einschränkung aufgehoben."; }} />
      )}
      {d.can_feedback && (
        <ActionButton label="Rückmeldung senden" icon={MessageCircle} testId="report-feedback" onDone={reload} title="Rückmeldung zur Meldung" submitLabel="Rückmeldung senden"
          page="rueckmeldung" pageSubtitle="Die Rückmeldung enthält keine privaten Angaben anderer Personen." pageSubject={deText(r.title)}
          pageRows={[["Empfänger", "Meldende Person"], ["Bezug", `Meldung „${deText(r.title)}“`],
            ["Status", r.feedback_sent_at ? `Zuletzt gesendet ${fmt(r.feedback_sent_at)}` : "Entwurf · Noch nicht gesendet"]]}
          description="Die meldende Person erhält eine neutrale Rückmeldung ohne Details zu Maßnahmen gegen andere."
          fields={[
            { name: "message", label: "Rückmeldung", type: "textarea", required: true,
              default: "Danke für deine Meldung. Wir haben sie geprüft und die nötigen Schritte eingeleitet." },
            { name: "internal_note", label: "Interne Notiz", type: "textarea" },
          ]}
          onSubmit={async (v) => { await api.post(`${base}/feedback`, v); return "Rückmeldung gesendet."; }} />
      )}
    </>
  );
}

const POST_OPEN = ["sichtbar", "ausgeblendet"];

// C59: own review page for a reported group post; decisions reuse the community moderation endpoint.
function PostReviewPage({ d, reload }) {
  const r = d.report;
  const c = d.context;
  if (!d.can_moderate || !c.community_post_id) return null;
  const open = POST_OPEN.includes(c.post_status);
  return (
    <PageView page="beitrag-pruefen" label="Gruppenbeitrag prüfen" icon={Eye} testId="report-post-review" className="mt-3"
      title="Gemeldeten Gruppenbeitrag prüfen" subtitle="Zeige nur den gemeldeten Ausschnitt mit notwendigem Zusammenhang." subject={deText(r.title)}
      aside={<>
        <BlockedAside icon={ShieldAlert} lavender title="Zuständigkeit" text={r.assigned_to?.name || "Noch nicht übernommen"} />
        <BlockedAside icon={Lock} title="Begrenzter Einblick" text="Nur der gemeldete Ausschnitt – keine weiteren privaten Nachrichten oder Profilangaben." />
      </>}>
      {({ back }) => (
        <>
          <PageRows testId="report-post-review" rows={[["Fall", deText(r.title)],
            ["Gemeldeter Inhalt", c.excerpt ? `„${c.excerpt.text}“` : "Ausschnitt nicht verfügbar"],
            ["Zusammenhang", `${c.type || "Gruppenbeitrag"} · ${c.source}${c.excerpt?.at ? ` · ${fmt(c.excerpt.at)}` : ""}`],
            ["Regelbezug", c.reason], ["Betroffenes Konto", c.reported?.name], ["Beitragsstatus", label(c.post_status || "unbekannt")]]} />
          <section className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3" data-testid="report-post-review-decision">
            <p className="text-xs font-semibold text-zinc-500">Entscheidung</p>
            {open ? (
              <div className="mt-2 flex flex-wrap gap-2"><ModButtons item={{ status: c.post_status }} path={`/community/posts/${c.community_post_id}/moderate`} prefix="report-post" reload={reload} /></div>
            ) : (
              <p className="mt-1 text-sm text-brand-purple" data-testid="report-post-review-closed">Am Beitrag ist keine weitere Massnahme möglich, weil er bereits entfernt wurde. Der Fall selbst bleibt über „Status ändern“ bearbeitbar.</p>
            )}
          </section>
          <PageButtons primary="Beitrag in der Moderation öffnen" primaryTo={`/community-beitraege/${c.community_post_id}`} back={back} testId="report-post-review" />
        </>
      )}
    </PageView>
  );
}

function ReportView({ d, id, reload }) {
  const r = d.report;
  const c = d.context;
  return (
    <>
      <PageHeader title={deText(r.title) || "Meldung"} subtitle={`Eingegangen ${fmt(r.created_at)}`} icon={ShieldAlert} actions={<Actions d={d} id={id} reload={reload} />} />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Fall" testId="report-case">
          <Info items={[["Dringlichkeit", <Status value={r.severity} />], ["Status", <Status value={r.status} />],
            ["Zuständig", r.assigned_to?.name], r.priority_reason && ["Begründung der Priorität", r.priority_reason],
            r.decision && ["Entscheidung", r.decision], r.closing_report && ["Abschlussbericht", r.closing_report],
            r.feedback_sent_at && ["Rückmeldung gesendet", fmt(r.feedback_sent_at)]]} />
        </Section>
        <Section title="Kontext (begrenzt)" testId="report-context">
          <Info items={[["Quelle", c.source], ["Grund", c.reason], ["Art", c.type], ["Meldende Person", c.reporter],
            c.reported && ["Betroffenes Konto", `${c.reported.name}${c.reported.write_restricted_until ? ` · eingeschränkt bis ${fmt(c.reported.write_restricted_until)}` : ""}`]]} />
          {c.excerpt && (
            <blockquote className="mt-4 border-l-4 border-brand-magenta/50 bg-secondary/60 p-3 text-sm" data-testid="report-excerpt">
              „{c.excerpt.text}“<footer className="mt-1 text-xs text-muted-foreground">gemeldeter Beitrag · {fmt(c.excerpt.at)}</footer>
            </blockquote>
          )}
          {c.community_post_id && (
            <Link to={`/community-beitraege/${c.community_post_id}`} data-testid="report-open-post" className="mt-2 inline-block text-sm font-medium text-brand-purple underline">
              Community-Beitrag in der Moderation öffnen
            </Link>
          )}
          <div><PostReviewPage d={d} reload={reload} /></div>
          {c.shared_fields && (
            <div className="mt-4 rounded-lg bg-secondary/60 p-3 text-sm" data-testid="report-shared-fields">
              <p className="mb-1 font-medium">Vom Support weitergegebene Felder</p>
              {Object.entries(c.shared_fields).map(([k, v]) => <p key={k}>{k}: {String(v ?? "–")}</p>)}
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Es wird nur der gemeldete Ausschnitt gezeigt – keine weiteren privaten Nachrichten.</p>
        </Section>
      </div>
      <Section title="Verlauf" className="mt-5" testId="report-history"><History items={r.history} /></Section>
    </>
  );
}

export default function ReportDetail() {
  const { id } = useParams();
  const st = useLoad(`/reports/${id}`);
  return (
    <div>
      <BackLink to="/meldungen-sicherheit">Zurück zu Meldungen</BackLink>
      <Loadable state={st}>{(d) => <ReportView d={d} id={id} reload={st.reload} />}</Loadable>
    </div>
  );
}
